import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import CaseCard from '@/components/base/CaseCard';
import { cases } from '@/mocks/cases';
import { categories, siteMeta } from '@/mocks/site';
import type { CaseItem } from '@/types/content';

const caseList = cases as unknown as CaseItem[];

export default function CasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const query = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return caseList.filter((item) => {
      const matchCategory = activeCategory === 'all' || item.categoryId === activeCategory;
      const matchKeyword =
        kw.length === 0 ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, query]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    updateParam('q', keyword.trim());
  };

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '吃瓜事件库',
      url: `${siteMeta.domain}/cases`,
      description: '按情感关系、合约风波、合作纠纷、商务代言、言论争议等类型整理的吃瓜事件合集。',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteMeta.domain },
        { '@type': 'ListItem', position: 2, name: '吃瓜事件库', item: `${siteMeta.domain}/cases` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: caseList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        url: `${siteMeta.domain}/case/${item.id}`,
      })),
    },
  ];

  return (
    <PageLayout>
      <SeoHead
        title="吃瓜事件库 | 分类筛选与搜索 · 911吃瓜"
        description="911吃瓜事件库按情感关系、合约风波、合作纠纷、商务代言、言论争议分类整理，支持关键词搜索，每个事件都配有关系图、矛盾链条与当事人回应。"
        keywords="吃瓜事件库,明星关系梳理,事件时间线,当事人回应,911吃瓜"
        path="/cases"
        jsonLd={jsonLd}
      />

      <section className="w-full bg-background-100 border-b border-background-200 pt-24 md:pt-28 pb-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumbs items={[{ label: '吃瓜事件库' }]} />
          <h1 className="mt-4 font-heading font-black text-3xl md:text-4xl text-foreground-950">吃瓜事件库</h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base leading-7 text-foreground-600">
            这里收录了正在被讨论的每一个话题。按类型筛选或直接搜索关键词，找到你想理清的那件事，点进去就能看到完整的人物关系图与矛盾链条。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => updateParam('category', 'all')}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                activeCategory === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => updateParam('category', cat.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={cat.icon} />
                {cat.name}
              </button>
            ))}
          </div>

          <form onSubmit={onSearch} className="flex items-center gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件关键词"
                aria-label="搜索吃瓜事件"
                className="w-full pl-10 pr-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 个事件
          {query && <span> · 关键词「{query}」</span>}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((item) => (
              <CaseCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="mt-10 text-center py-16 bg-background-100 border border-background-200 rounded-lg">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-emotion-sad-line" />
            </span>
            <p className="mt-4 font-heading font-bold text-foreground-900">暂时没有匹配的事件</p>
            <p className="mt-2 text-sm text-foreground-500">换个关键词或选择其他分类试试。</p>
            <Link
              to="/cases"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              重置筛选
            </Link>
          </div>
        )}
      </section>
    </PageLayout>
  );
}